import { createContext, useContext, useEffect, useRef, useState } from "react"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "@/contexts/AuthContext"

type Theme = "dark" | "light" | "system"

type ThemeProviderProps = {
    children: React.ReactNode
    defaultTheme?: Theme
    storageKey?: string
}

type ThemeProviderState = {
    theme: Theme
    setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
    theme: "light",
    setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
    children,
    defaultTheme = "light",
    storageKey = "vite-ui-theme",
}: ThemeProviderProps) {
    const [theme, setTheme] = useState<Theme>(
        () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
    )

    // Apply theme to DOM immediately (no isLoaded wait)
    useEffect(() => {
        const root = window.document.documentElement

        root.classList.remove("light", "dark")

        if (theme === "system") {
            const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
                .matches
                ? "dark"
                : "light"

            root.classList.add(systemTheme)
            return
        }

        root.classList.add(theme)
    }, [theme])

    // Auth restores its session asynchronously, so the user ID is not available on the
    // first render after a reload or right after login. Keying the load on it makes the
    // saved theme apply as soon as the user is known, instead of once at mount.
    const { user } = useAuth()
    const userId = user?.id

    // Bumped on every local change so a slower remote load cannot overwrite it
    const localChangeRef = useRef(0)

    // Load theme from Supabase in background (non-blocking)
    useEffect(() => {
        if (!userId) return

        let cancelled = false
        const changeCountAtStart = localChangeRef.current

        const loadThemeFromSupabase = async () => {
            try {
                const { data: settings, error } = await supabase
                    .from('user_settings')
                    .select('theme_preference')
                    .eq('user_id', userId)
                    .maybeSingle()

                if (error) throw error

                const remoteTheme = settings?.theme_preference as Theme | null | undefined
                if (cancelled || !remoteTheme || localChangeRef.current !== changeCountAtStart) return

                setTheme(remoteTheme)
                localStorage.setItem(storageKey, remoteTheme)
            } catch (error) {
                console.error('Failed to load theme from Supabase:', error)
            }
        }

        loadThemeFromSupabase()

        return () => {
            cancelled = true
        }
    }, [userId, storageKey])

    const value = {
        theme,
        setTheme: async (newTheme: Theme) => {
            localChangeRef.current += 1

            // Update local state and localStorage immediately
            localStorage.setItem(storageKey, newTheme)
            setTheme(newTheme)

            // Sync to Supabase in background
            try {
                const { data: { user } } = await supabase.auth.getUser()

                if (user) {
                    // Use upsert to handle both create and update cases
                    const { error } = await supabase
                        .from('user_settings')
                        .upsert({
                            user_id: user.id,
                            theme_preference: newTheme
                        } as any, {
                            onConflict: 'user_id'
                        })

                    if (error) {
                        console.error('Failed to save theme to Supabase:', error)
                    }
                }
            } catch (error) {
                console.error('Failed to save theme to Supabase:', error)
                // Theme still works locally even if Supabase sync fails
            }
        },
    }

    return (
        <ThemeProviderContext.Provider value={value}>
            {children}
        </ThemeProviderContext.Provider>
    )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
    const context = useContext(ThemeProviderContext)

    if (context === undefined)
        throw new Error("useTheme must be used within a ThemeProvider")

    return context
}
